/**
 * CampaignDetailPage — one campaign's task graph, rendered verbatim.
 *
 * Campaign `auteur-cloud-commercial-v1`, task C05C. The task graph is the
 * server's; this page draws nodes and their `depends_on` edges exactly as
 * reported and never infers a task status that was not returned. The
 * `source_local` badge rule from the list page applies here too.
 *
 * Also renders the full federated telemetry the server returns (M02, campaign
 * `auteur-campaigns-monitor-b02a-v1`) through the same components the list
 * uses, plus the server's `diagnostic_note` when the workspace was degraded.
 * Read-only: no lifecycle control lives on this page.
 *
 * H02 (campaign `auteur-owner-history-index-v1`): the header carries the
 * server's provenance badge and the merged timeline below renders live and
 * imported evidence with each row's own badge; gaps stay visible as gaps.
 *
 * BUILD STATUS: compiled and exercised as part of the Vite customer SPA.
 */

import React, { useCallback, useEffect, useState } from 'react';

import { OperationsApiError, fetchCampaign } from '../../api/operations';
import {
  CampaignBadges,
  CampaignTelemetry,
  DiagnosticNote,
  EventTail,
  MergedTimeline,
} from './CampaignsPage';

function CampaignDetailPage({ campaignId }) {
  const [campaign, setCampaign] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!campaignId) return;
    setError(null);
    try {
      setCampaign(await fetchCampaign(campaignId));
    } catch (err) {
      if (err instanceof OperationsApiError && err.isUnavailable) {
        setError('unavailable');
      } else {
        setError(err instanceof Error ? err.message : 'the campaign could not be loaded');
      }
      setCampaign(null);
    }
  }, [campaignId]);

  useEffect(() => {
    load();
  }, [load]);

  if (error === 'unavailable') {
    return (
      <section>
        <p className="unavailable">
          Unavailable — the operations backend is not mounted for this deployment.
        </p>
      </section>
    );
  }

  if (campaign === null) {
    return <section><p>{error ? error : 'Loading…'}</p></section>;
  }

  return (
    <section>
      <h1>
        {campaign.title}
        <CampaignBadges campaign={campaign} />
        <span className="badge">{campaign.status}</span>
      </h1>
      <DiagnosticNote campaign={campaign} />
      <CampaignTelemetry campaign={campaign} />
      <h2>Merged timeline</h2>
      <MergedTimeline campaign={campaign} />
      <h2>Recent events</h2>
      <EventTail events={campaign.live_event_tail} />
      <h2>Task graph</h2>
      {campaign.task_graph.length === 0 ? (
        <p>The server reported no tasks for this campaign.</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Task</th>
              <th>Status</th>
              <th>Depends on</th>
            </tr>
          </thead>
          <tbody>
            {campaign.task_graph.map((node) => (
              <tr key={node.task_key}>
                <td>{node.task_key}</td>
                <td>{node.status}</td>
                <td>{node.depends_on.join(', ')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

export default CampaignDetailPage;
